import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { TaskModal } from './Tasks';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths,
  format, isSameMonth, isSameDay, isToday, parseISO,
} from 'date-fns';

const PRIORITY_COLOR = { low: '#3368b8', medium: '#d99a1f', high: '#ec2013' };

const CalendarModule = () => {
  const { user } = useAuth();
  const { lobs } = useData();
  const [month, setMonth] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState('');
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [modalDefaultDate, setModalDefaultDate] = useState(null);

  const load = useCallback(() => {
    api.get('/tasks').then((r) => setTasks(r.data.filter((t) => t.due_date))).catch((e) => setErr(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.role === 'super_admin') api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
  }, [user?.role]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    const arr = [];
    let d = start;
    while (d <= end) { arr.push(d); d = addDays(d, 1); }
    return arr;
  }, [month]);

  const tasksByDay = (day) => tasks.filter((t) => { try { return isSameDay(parseISO(t.due_date), day); } catch { return false; } });
  const dayTasksForPanel = selectedDay ? tasksByDay(selectedDay) : [];

  return (
    <>
      <div className="page-toolbar" data-testid="calendar-toolbar">
        <span className="page-title">Calendar</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="dp-btn" onClick={() => setMonth((m) => subMonths(m, 1))} data-testid="cal-prev-month"><ChevronLeft size={12} /></button>
          <span style={{ fontSize: 12, fontWeight: 600, minWidth: 120, textAlign: 'center' }} data-testid="cal-month-label">{format(month, 'MMMM yyyy')}</span>
          <button className="dp-btn" onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="cal-next-month"><ChevronRight size={12} /></button>
          <button className="dp-btn" onClick={() => setMonth(new Date())} data-testid="cal-today-btn">Today</button>
        </div>
        <button className="cta-btn" onClick={() => { setModalDefaultDate(null); setShowModal(true); }} data-testid="cal-add-task-btn">
          <Plus size={11} style={{ marginRight: 4 }} />New task
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="calendar-error">{err}</div>}

      <div className="content-area" data-testid="calendar-content" style={{ display: 'flex', gap: 14 }}>
        <div className="cal-grid" data-testid="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div className="cal-dow" key={d}>{d}</div>)}
          {days.map((day) => {
            const dayTasks = tasksByDay(day);
            const inMonth = isSameMonth(day, month);
            const key = format(day, 'yyyy-MM-dd');
            return (
              <div
                key={key}
                className={`cal-day${inMonth ? '' : ' outside'}${isToday(day) ? ' today' : ''}${selectedDay && isSameDay(selectedDay, day) ? ' selected' : ''}`}
                onClick={() => setSelectedDay(day)}
                data-testid={`cal-day-${key}`}
              >
                <div className="cal-day-num">{format(day, 'd')}</div>
                {dayTasks.slice(0, 3).map((t) => (
                  <div key={t.id} className="cal-task-chip" style={{ background: PRIORITY_COLOR[t.priority] }} data-testid={`cal-task-chip-${t.id}`} title={t.title}>
                    {t.title}
                  </div>
                ))}
                {dayTasks.length > 3 && <div className="cal-more">+{dayTasks.length - 3} more</div>}
              </div>
            );
          })}
        </div>

        <div className="card" style={{ width: 260, flexShrink: 0 }} data-testid="calendar-day-panel">
          <div className="card-hd">{selectedDay ? format(selectedDay, 'EEEE, MMM d') : 'Select a day'}</div>
          {selectedDay && (
            <>
              {dayTasksForPanel.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-faded)', padding: '6px 0' }}>No tasks due this day.</div>}
              {dayTasksForPanel.map((t) => (
                <div key={t.id} className="field-row" data-testid={`cal-panel-task-${t.id}`}>
                  <span className="fl">{t.title}</span>
                  <span className="fv">{t.priority}</span>
                </div>
              ))}
              <button className="dp-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
                      onClick={() => { setModalDefaultDate(format(selectedDay, 'yyyy-MM-dd')); setShowModal(true); }}
                      data-testid="cal-add-task-for-day-btn">
                <Plus size={11} style={{ marginRight: 4 }} />Add task for this day
              </button>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <TaskModal
          task={modalDefaultDate ? { due_date: modalDefaultDate, priority: 'medium', title: '', description: '', lob_id: '', contact_id: '', assignee_id: '' } : null}
          lobs={lobs} users={users} isAdmin={user?.role === 'super_admin'}
          onClose={(saved) => { setShowModal(false); if (saved) load(); }}
        />
      )}
    </>
  );
};

export default CalendarModule;
